import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, Lightbulb } from 'lucide-react';
import ChessBoardView from '@/components/chess/ChessBoardView';

export default function ExerciseSolver() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ex, setEx] = useState(null);
  const [game, setGame] = useState(null);
  const [status, setStatus] = useState('playing'); // playing|correct|wrong
  const [hintShown, setHintShown] = useState(false);
  const [moves, setMoves] = useState([]);
  const startTime = useMemo(() => Date.now(), [id]);

  useEffect(() => {
    api.get(`/exercises/${id}`).then(r => {
      setEx(r.data);
      setGame(new Chess(r.data.fen));
      setStatus('playing');
      setMoves([]);
      setHintShown(false);
    });
  }, [id]);

  const onDrop = (from, to) => {
    if (!game || status !== 'playing') return false;
    const tmp = new Chess(game.fen());
    let move;
    try {
      move = tmp.move({ from, to, promotion: 'q' });
    } catch { return false; }
    if (!move) return false;
    setGame(tmp);
    const nextMoves = [...moves, move.san];
    setMoves(nextMoves);
    // F1 · múltiples respuestas correctas: aceptar si la jugada coincide con la
    // siguiente jugada de ALGUNA línea aceptada que comparte el prefijo jugado.
    const norm = (s) => (s || '').replace(/[+#!?]/g, '').trim();
    const i = moves.length;
    const lines = (ex.accepted_solutions && ex.accepted_solutions.length)
      ? ex.accepted_solutions
      : [ex.solution_moves || []];
    const viable = lines.filter(ln => ln.length > i && ln.slice(0, i).every((s, k) => norm(s) === norm(moves[k])));
    const matched = viable.some(ln => norm(ln[i]) === norm(move.san));
    if (matched) {
      const complete = viable.some(ln => norm(ln[i]) === norm(move.san) && ln.length === nextMoves.length);
      if (complete) {
        setStatus('correct');
        submit(true, nextMoves);
      }
      // parcial correcto: sigue siendo el turno del alumno (status 'playing')
    } else {
      setStatus('wrong');
      submit(false, nextMoves);
    }
    return true;
  };

  const submit = async (correct, mvs) => {
    try {
      const { data } = await api.post('/exercises/attempt', {
        exercise_id: id, moves: mvs, correct, time_ms: Date.now() - startTime
      });
      if (correct) {
        toast.success(`¡Correcto! +${data.points_awarded || 0} pts`);
      } else {
        toast.error('Movimiento incorrecto. Intenta otra vez.');
      }
    } catch {}
  };

  const reset = () => {
    setGame(new Chess(ex.fen));
    setStatus('playing');
    setMoves([]);
  };

  if (!ex || !game) return <div className="p-8">Cargando…</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Ejercicio · {ex.difficulty}</div>
        <h1 className="font-display text-3xl font-extrabold" data-testid="exercise-title">{ex.title}</h1>
        <p className="text-slate-600 mt-2">{ex.description}</p>
      </div>
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <ChessBoardView fen={game.fen()} onPieceDrop={onDrop} boardWidth={560}
            orientation={ex.fen.split(' ')[1] === 'w' ? 'white' : 'black'}/>
        </div>
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Estado</div>
            <div className="font-display text-2xl font-extrabold" data-testid="exercise-status">
              {status === 'playing' && 'Tu turno'}
              {status === 'correct' && <span className="text-emerald-600 flex items-center gap-2"><CheckCircle2/> ¡Resuelto!</span>}
              {status === 'wrong' && <span className="text-rose-600">Intenta de nuevo</span>}
            </div>
            <div className="mt-4 font-mono text-sm text-slate-700 min-h-[24px]">
              {moves.join(' ') || '—'}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold">Pista</div>
              <Button data-testid="exercise-hint-btn" variant="ghost" size="sm" onClick={() => setHintShown(true)}>
                <Lightbulb className="w-4 h-4 mr-1"/> Mostrar pista
              </Button>
            </div>
            <div className="text-sm text-slate-600 min-h-[40px]">
              {hintShown ? (ex.explanation || `Piensa en la mejor jugada. Solución: ${ex.solution_moves.join(' ')}`) : '—'}
            </div>
          </div>
          <div className="flex gap-3">
            <Button data-testid="exercise-reset-btn" onClick={reset} variant="outline" className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-2"/> Reiniciar
            </Button>
            <Button data-testid="exercise-next-btn" onClick={() => nav('/student/exercises')} className="rounded-full bg-blue-600 hover:bg-blue-700 border-b-4 border-blue-800 btn-tactile">
              Volver
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
